import { useMemo, useState } from 'react'
import { Card, Table, Form, Alert, Spinner } from 'react-bootstrap'
import { toast } from 'react-toastify'
import {
  useCreateAdminUserSection,
  useDeleteAdminUserSection
} from 'api/hooks/use-admin-user-sections'
import { errorMessage } from 'admin/shared/errors'

const UserSectionsManager = ({ user }) => {
  const userId = user?.id
  const userSections = user?.user_sections ?? []
  const availableSections = user?.available_sections ?? []

  // Track which row is currently being updated so we can disable just that
  // checkbox and show a spinner next to it instead of locking the whole grid.
  const [pendingSectionId, setPendingSectionId] = useState(null)

  const sectionIdToUserSection = useMemo(() => {
    const map = new Map()
    userSections.forEach((us) => map.set(us.section_id, us))
    return map
  }, [userSections])

  const { mutate: addSection } = useCreateAdminUserSection({
    userId,
    options: {
      onSettled: () => setPendingSectionId(null),
      onSuccess: () => toast.success('Section added'),
      onError: (err) => toast.error(errorMessage(err, 'Failed to add section'))
    }
  })

  const { mutate: removeSection } = useDeleteAdminUserSection({
    userId,
    options: {
      onSettled: () => setPendingSectionId(null),
      onSuccess: () => toast.success('Section removed'),
      onError: (err) => toast.error(errorMessage(err, 'Failed to remove section'))
    }
  })

  const handleToggle = (section) => (e) => {
    setPendingSectionId(section.id)
    if (e.target.checked) {
      addSection(section.id)
    } else {
      const existing = sectionIdToUserSection.get(section.id)
      if (existing) removeSection(existing.id)
    }
  }

  return (
    <Card className="mb-4">
      <Card.Header><h5 className="mb-0">Sections</h5></Card.Header>
      <Card.Body>
        {availableSections.length === 0 ? (
          <Alert variant="info" className="mb-0">
            This user&apos;s municipality has no sections configured.
          </Alert>
        ) : (
          <>
            <p className="text-muted small mb-3">
              {user?.user_type === 'accounting'
                ? 'Sections across all of this user\'s municipalities are listed.'
                : 'Only sections the user\'s municipality has access to are listed.'}
            </p>
            <Table bordered hover responsive className="mb-0">
              <thead>
                <tr>
                  <th>Section</th>
                  <th>Abbreviation</th>
                  <th className="text-center" style={{ width: '8rem' }}>Access</th>
                </tr>
              </thead>
              <tbody>
                {availableSections.map((section) => {
                  const isAssigned = sectionIdToUserSection.has(section.id)
                  const isPending = pendingSectionId === section.id
                  return (
                    <tr key={section.id}>
                      <td>{section.name}</td>
                      <td><code>{section.abbreviation}</code></td>
                      <td className="text-center">
                        {isPending ? (
                          <Spinner animation="border" size="sm" />
                        ) : (
                          <Form.Check
                            type="checkbox"
                            aria-label={`Toggle ${section.name} access`}
                            checked={isAssigned}
                            disabled={pendingSectionId !== null}
                            onChange={handleToggle(section)}
                          />
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </Table>
          </>
        )}
      </Card.Body>
    </Card>
  )
}

export default UserSectionsManager
